export type AnalyticsConsent = "accepted" | "essential";
export type CookieConsentPreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

export const ANALYTICS_CONSENT_STORAGE_KEY = "laszti_analytics_consent";
export const ANALYTICS_CONSENT_EVENT = "laszti:analytics-consent";
export const COOKIE_CONSENT_SETTINGS_EVENT = "laszti:cookie-consent-settings";
export const COOKIE_CONSENT_PREFERENCES_STORAGE_KEY = "laszti_cookie_preferences";

const essentialPreferences: CookieConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const acceptedPreferences: CookieConsentPreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
};

export function readAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
  return value === "accepted" || value === "essential" ? value : null;
}

export function readCookieConsentPreferences(): CookieConsentPreferences {
  if (typeof window === "undefined") return essentialPreferences;
  const stored = window.localStorage.getItem(COOKIE_CONSENT_PREFERENCES_STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Partial<CookieConsentPreferences>;
      return {
        necessary: true,
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
      };
    } catch {
      // Fall through to the analytics consent key for backward compatibility.
    }
  }

  return readAnalyticsConsent() === "accepted" ? acceptedPreferences : essentialPreferences;
}

export function hasAnalyticsConsent() {
  return readAnalyticsConsent() === "accepted";
}

export function writeAnalyticsConsent(consent: AnalyticsConsent) {
  window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent);
  window.localStorage.setItem(
    COOKIE_CONSENT_PREFERENCES_STORAGE_KEY,
    JSON.stringify(consent === "accepted" ? acceptedPreferences : essentialPreferences),
  );
  window.dispatchEvent(new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, { detail: consent }));
}

export function writeCookieConsentPreferences(preferences: CookieConsentPreferences) {
  const normalized: CookieConsentPreferences = {
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
  };
  const analyticsConsent: AnalyticsConsent = normalized.analytics ? "accepted" : "essential";
  window.localStorage.setItem(COOKIE_CONSENT_PREFERENCES_STORAGE_KEY, JSON.stringify(normalized));
  window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, analyticsConsent);
  window.dispatchEvent(new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, { detail: analyticsConsent }));
}

export function openCookieConsentSettings() {
  window.dispatchEvent(new Event(COOKIE_CONSENT_SETTINGS_EVENT));
}
